import IconSpan from '../components/IconSpan'
import Seo from '../components/Seo'
import VideoWithPlaceholder from '../components/VideoWithPlaceholder'
import './featurespage.css'
import { useState } from 'react'

const FeaturesPage = (): JSX.Element => {
	const [modalOpen, setModalOpen] = useState(false)
	const handleImgClick = () => setModalOpen(true)
	const handleModalClose = () => setModalOpen(false)
	return (
		<div className='features-page'>
			<Seo
				title='Features | npChatbot for Serato DJ and Twitch'
				description='Explore npChatbot features including Twitch music discovery commands, Serato playlist stats, Spotify playlist creation, OBS integration, and Discord sharing.'
				path='/features'
			/>
			<div className='features-page-header-container'>
				<div className='features-page-feature-main'>
					<h1 className='features-page-header main-header page-title'>
						What is npChatbot anyway?
					</h1>
					<div className='features-page-feature-main main-header-text'>
						<p>
							npChatbot (<span className='highlighted-text'>"now playing"</span>
							) is a desktop app that adds a suite of interactive chat commands
							to your Twitch
							<IconSpan /> channel during your live streamed DJ sets.
						</p>
						<p>
							Under the hood, npChatbot links your{' '}
							<a
								className='serato-link'
								href='https://support.serato.com/hc/en-us/articles/228019568-Live-Playlists'
								rel='noreferrer'
								target='_blank'
							>
								<span className='highlighted-text'>
									Serato
									<IconSpan /> Live Playlist
								</span>
							</a>{' '}
							data to your Twitch
							<IconSpan /> channel's chat, enabling an interactive connection
							between the music you're playing and your viewers in real time.
						</p>
					</div>
				</div>
				<div className='features-page-feature video-element'>
					<VideoWithPlaceholder
						src='/videos/npchatbot-demo-01.mp4'
						ariaLabel='npChatbot overview video'
					/>
				</div>
			</div>

			<div className='features-page-container'></div>

			{/* <hr /> */}

			<div className='features-page-container'>
				<div className='features-page-feature'>
					<h2 className='features-page-subheader'>
						Interactive Music Discovery
					</h2>
					<p>
						By opening up access to your play history during your live-streamed
						DJ sets, viewers can directly interact with that history, adding a
						unique layer of interactivity and music discovery to each stream.
					</p>
					<p>
						The <span className='highlighted-text'>!np</span> command and its
						options allow your viewers to see the song currently playing, the
						previous song played (in case they missed it!), the song you began
						your set with, and others.
					</p>
					<p>
						There's also a "vibecheck" option that returns a random selection
						from your play history (title/artist) and how long ago you played
						it, giving your viewers insight into your set's current mood or
						tone.
					</p>
				</div>
				<div className='features-page-video'>
					<VideoWithPlaceholder
						src='/videos/npchatbot-demo-02.mp4'
						ariaLabel='Interactive Music Discovery demo video'
					/>
				</div>
			</div>
			<div className='features-page-container'>
				<div className='features-page-feature'>
					<h2 className='features-page-subheader'>Search By Song Or Artist</h2>
					<p>
						When using the <span className='highlighted-text'>!dyp</span>{' '}
						command (short for "did you play...?") followed by an artist's name
						or a song title, your viewers will see the total number of songs
						that match the result along with the most recent song played and how
						long ago it was played.
					</p>
					<p>
						This gives your viewers the ability to digitally "dig" through your
						history at any time during your set, providing you, the streaming
						DJ, with on-the-fly insight as to what music your current viewers
						are signaling an interest in.
					</p>
				</div>
				<div className='features-page-video'>
					<VideoWithPlaceholder
						src='/videos/dyp-command-video.mp4'
						ariaLabel='Search by song or artist demo video'
					/>
				</div>
			</div>
			<div className='features-page-container'>
				<div className='features-page-feature'>
					<h2 className='features-page-subheader'>Real Time Playlist Stats</h2>
					<p>
						Using the commands in npChatbot, your viewers (or you!) can check
						the
						<span className='highlighted-text'> stats</span> for your DJ set at
						any time during your stream, showing the total number of songs
						played so far and the average song length for your current set.
					</p>
					<p>
						For a deeper dive, viewers have access to commands that determine
						the <span className='highlighted-text'>shortest song</span> and{' '}
						<span className='highlighted-text'>longest song</span> played so far
						in your set, as well as how many times you've rocked{' '}
						<span className='highlighted-text'>doubles</span> during your set
						and which songs you were cutting it up with when you did.
					</p>
				</div>
				<div className='features-page-video'>
					<VideoWithPlaceholder
						src='/videos/doubles_command_video.mp4'
						ariaLabel='Real time playlist stats demo video'
					/>
				</div>
			</div>
			<div className='features-page-container'>
				<div className='features-page-feature'>
					<h2 className='features-page-subheader'>
						Create Spotify
						<IconSpan /> Playlists From Your DJ Sets
					</h2>
					<p>
						Linking npChatbot to your{' '}
						<span className='highlighted-text'>
							Spotify
							<IconSpan />
						</span>{' '}
						account gives you the ability to create and share a playlist of your
						live set with your{' '}
						<span className='highlighted-text'>
							Twitch
							<IconSpan />
						</span>{' '}
						community any time that you stream.
					</p>
					<p>
						With this feature enabled, npChatbot will create a new playlist from
						your{' '}
						<span className='highlighted-text'>
							Serato
							<IconSpan />
						</span>{' '}
						play history when you connect npChatbot to your chat.
					</p>
					<p>
						npChatbot will then regularly update the playlist with each new song
						you play, and you can share this playlist with your viewers at any
						time using the{' '}
						<span className='highlighted-text'>!np playlist</span> command.
					</p>
				</div>
				<div className='features-page-video'>
					<VideoWithPlaceholder
						src='/videos/spotify_demo.mp4'
						ariaLabel='Spotify playlist creation demo video'
					/>
				</div>
			</div>
			<div className='features-page-container'>
				<div className='features-page-feature'>
					<h2 className='features-page-subheader'>Auto ID & Tag Clean-Up</h2>
					<p>
						With the <span className='highlighted-text'>Auto ID</span> feature
						enabled, npChatbot will send a message to your channel's chat
						identifying the current song playing each time it updates.
					</p>
					<p>
						Additionally, npChatbot also gives streamers the options to clean up
						the file tag text used in this feature, removing any extra text
						found in parentheses or brackets within the title or artist fields
						before sending the info to the chat.
					</p>
				</div>
				<div className='features-page-feature'>
					<h2 className='features-page-subheader'>OBS Integration</h2>
					<p>
						For streamers using OBS, npChatbot can be configured to send each
						command's response to OBS for use in overlays in your live streams.
					</p>
					<p>
						With{' '}
						<a
							className='serato-link'
							href='https://obsproject.com/kb/remote-control-guide'
							rel='noreferrer'
							target='_blank'
						>
							<span className='highlighted-text'>OBS WebSockets</span>
						</a>{' '}
						enabled, you can enter your OBS websocket address (and password if
						secured) which will allow npChatbot to send each command's response
						to OBS for use on-screen as part of a{' '}
						<a
							className='serato-link'
							href='https://obsproject.com/kb/text-sources'
							rel='noreferrer'
							target='_blank'
						>
							<span className='highlighted-text'>Text (GDI+)</span>
						</a>{' '}
						element.
					</p>
				</div>
			</div>
			<div className='features-page-container'>
				<div className='features-page-feature'>
					<h2 className='features-page-subheader'>
						Playlist Analytics & Discord
						<IconSpan /> Sharing
					</h2>
					<p>
						With npChatbot's <span className='highlighted-text'>Analytics</span>{' '}
						feature, you can review your final playlist "stats" and your
						viewers' command use for each of your DJ streams.
					</p>
					<p>
						You can also do a deeper dive into what you play on-stream during
						your DJ sets, with the ability to search by title, artist, and more
						over any time range you specify.
					</p>
					<p>
						This additional insight gives you, the streaming DJ, a better look
						into what music your viewers are really connecting with and engaging
						with most during your streams.
					</p>
					<p>
						If you've created a{' '}
						<span className='highlighted-text'>
							Spotify
							<IconSpan />
						</span>{' '}
						playlist of your stream, the link is included in your summary which
						you can easily share with your community by linking npChatbot with
						your{' '}
						<span className='highlighted-text'>
							Discord
							<IconSpan />
						</span>{' '}
						server!
					</p>
				</div>
				<div className='features-page-feature'>
					<img
						src='/images/installation/NEW_analytics_01.png'
						alt='Playlist Analytics'
						width='85%'
						height='85%'
						style={{ cursor: 'pointer' }}
						onClick={handleImgClick}
					/>
					{modalOpen && (
						<div className='modal-overlay' onClick={handleModalClose}>
							<img
								className='modal-img'
								src='/images/installation/NEW_analytics_01.png'
								alt='Playlist Analytics Full Size'
							/>
							<span className='modal-close'>&#10005;</span>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default FeaturesPage
